import { applyMiddleware, createStore } from '@reduxjs/toolkit';
import axios, { AxiosRequestConfig } from 'axios';
import isEqual from 'fast-deep-equal/react';
import React from 'react';
import { composeWithDevTools } from 'redux-devtools-extension';
import qs from "qs";
import _ from "lodash";

class PawModel {
  static apiType: string;

  static d3Compatible = true;

  id: string;

  attributes: any;

  type!: string;

  writers!: string[];

  readers!: string[];

  created_at!: string;

  updated_at!: string;

  private __populated: boolean = false;

  public relationships: string[] = [];

  static _store?: any;

  static currentPage: number = 1;

  static links: {
    first: null | string;
    last: null | string;
    next: null | string;
    prev: null | string;
    self: null | string;
  } = {
    first: null,
    last: null,
    next: null,
    prev: null,
    self: null,
  }

  private static _cache: any;

  static get cache() {
    if (!this._cache) {
      this._cache = {};
    }
    return this._cache;
  }

  static clearCache() {
    delete this._cache;
  }

  constructor(values: any) {
    this.id = values.id;
    this.attributes = values.attributes;

    return new Proxy(this, {
      get: (target: any, key: string) => {
        const _getter = (slug: string) => {
          if (typeof slug === 'string' && Reflect.ownKeys(target.relationships).includes(slug)) {
            return _.get(target.relationships, slug);
          }

          return _.get(target.attributes, slug) !== undefined ? _.get(target.attributes, slug) : _.get(target, slug);
        }

        switch (key) {
          case 'prototype':
            return target.constructor;
          case 'constructor':
            return target.constructor;
          case 'attributes':
            return target.attributes;
          case 'relationships':
            return values.relationships;
          case '_raw':
            return values;
          case 'id':
            return target.id;
          case 'get':
            return _getter;
          default:
            break;
        }

        return _getter(key);
      },
    });
  }

  static get store() {
    if (!this._store) {
      type StateType = {
        list: PawModel[];
        loading: boolean;
      };
      const initialState: StateType = {
        list: [],
        loading: false,
      };
      const preloadedStates: any = undefined;
      const middlewares: any[] = [];
      const composeEnhancers = composeWithDevTools({
        name: this.toString()
          .split('(' || /s+/)[0]
          .split(' ' || /s+/)[1],
      });
      const composedEnhancers = composeEnhancers(applyMiddleware(...middlewares));
      this._store = createStore(
        (state: any = initialState, { type, payload }: any) => {
          switch (type) {
            case 'LOAD':
              return { ...state, loading: true };
            case 'UNLOAD':
              return { ...state, loading: false };
            case 'UPSERT':
              const upsert = (item: PawModel) => {
                const found = state.list.find((i: PawModel) => i.id === item.id);
                if (found) {
                  state = {
                    ...state,
                    list: state.list.map((i: PawModel) => (i === found ? item : i)),
                  };
                } else {
                  state = { ...state, list: [...state.list, item] };
                }
              };

              if (Array.isArray(payload)) {
                payload.forEach((item) => upsert(new this(item)));
              } else {
                upsert(new this(payload));
              }

              return state;
            case 'UPDATE':
              const update = (item: PawModel) => {
                const found = state.list.find((i: PawModel) => i.id === item.id);
                if (found) {
                  state = {
                    ...state,
                    list: state.list.map((i: PawModel) => {
                      if (i === found) {
                        Object.assign(i, payload);
                      }

                      return i;
                    }),
                  };
                }
              };

              update(payload);

              return state;
            case 'DELETE':
              const found = state.list.find((i: PawModel) => i.id === payload.id);
              return { ...state, list: [...state.list.filter((i: PawModel) => i !== found)] };
            case 'REINIT':
              return { list: [] };
            default:
              return state;
          }
        },
        preloadedStates,
        composedEnhancers
      );
    }

    return this._store;
  }

  static get list() {
    return this.store.getState().list;
  }

  static async create(payload: object, handleError = false) {
    try {
      const {
        data: { data },
      } = await axios.post(`/api/d2/${this.apiType}`, { data: { attributes: payload } });
      const instance = new this(data);
      this.clearCache();
      this.store.dispatch({ type: 'UPSERT', payload: data });
      return instance;
    } catch (e) {
      if (handleError) {
        alert('Une erreur est survenue, impossible de créer cet élément');
        console.error(e);
        return undefined;
      }

      throw e;
    }
  }

  static get(id: string, fetch?: boolean) {
    const item = !fetch && this.list.find((model: PawModel) => model.id === id);

    if (!item && fetch !== false) {
      return new Promise(async (resolve, reject) => {
        try {
          const res = await this.fetch(id, false);
          resolve(this.get(res.id, false));
        } catch (e) {
          reject(e);
        }
      });
    }

    return item;
  }

  static async fetch(query: object | string, count = true, refresh = false) {
    const cacheKey = JSON.stringify({ query, count });
    if (this.cache[cacheKey] && !refresh) {
      return this.cache[cacheKey];
    }

    try {
      let opt: AxiosRequestConfig = {
        method: 'GET'
      };

      if (typeof query === 'string') {
        opt.url = `/api/d2/${this.apiType}/${query}`;
      } else {
        // @ts-ignore
        if (this.d3Compatible && (query.search || query.title)) {
          opt.method = 'POST'
          opt.url = `/api/d3/search/${this.apiType}`;
          opt.data = query;
        } else {
          const params = qs.stringify(query, { arrayFormat: 'brackets' });
          opt.url = `/api/d2/${this.apiType}?${params}`;
        }
      }

      const response = await axios(opt);
      const { data } = response;
      // @ts-ignore
      // const upsertStore = !this.d3Compatible || (!query.search && !query.title);
      const formattedData = this.setDatas(data);
      let res;
      if (count) {
        res = {
          count: data.meta.pagination.total_objects,
          data: formattedData
        }
      } else {
        res = formattedData;
      }
      this.cache[cacheKey] = res;
      return this.cache[cacheKey];
    } catch (e) {
      return [];
    }
  }

  static async bulkUpdate(query: any, payload: any, url?: string) {
    url = url || `/api/d2/${this.apiType}`;
    let items = query;
    if (!Array.isArray(query)) {
      const { data } = await this.fetch(query);
      items = data.map((i: any) => i.id);
    }

    const requests = items.map((id: string) => ({
      "method": "PATCH",
      "url": `${url}/${id}`,
      "body": {
        data: { attributes: payload },
      }
    }))
    await axios.post(`/api/d2/batch_sequential`, { requests });
  }

  private static setDatas(axiosDatas: any, upsertStore = true) {
    try {
      const { data, meta, links } = axiosDatas;
      // Original function
      if (upsertStore) {
        this.store.dispatch({ type: 'UPSERT', payload: data });
      }
      if (Array.isArray(data)) {
        return data.map((item: PawModel) => this.list.find((_item: PawModel) => _item.id === item.id) || new this(item));
      }
      return this.list.find((_item: PawModel) => _item.id === data.id) || new this(data);
    } catch (error) {
      return [];
    }
  }

  async populate() {
    if (this.__populated) {
      return;
    }

    try {
      const {
        data: { data },
      } = await axios.get(`/api/d2/${Object.getPrototypeOf(this).constructor.apiType}/${this.id}`);
      Object.assign(this, data);
      this.__populated = true;
    } catch (e) {
      alert('Une erreur est survenue, impossible de peupler cet élément');
      console.error(e);
    }
  }

  async update(payload: any) {
    const { constructor } = Object.getPrototypeOf(this);
    try {
      const {
        data: { data },
      } = await axios.patch(`/api/d2/${Object.getPrototypeOf(this).constructor.apiType}/${this.id}`, {
        data: { attributes: payload },
      });
      constructor.store.dispatch({ type: 'UPDATE', payload: data });
      constructor.clearCache();
    } catch (e) {
      alert('Une erreur est survenue, impossible de mettre à jour cet élément');
      console.error(e);
    }
  }

  async delete(force = false) {
    if (!force && !window.confirm('Voulez-vous vraiment supprimer cet élément ?')) {
      return;
    }

    const { constructor } = Object.getPrototypeOf(this);
    try {
      await axios.delete(`/api/d2/${Object.getPrototypeOf(this).constructor.apiType}/${this.id}`);
      constructor.store.dispatch({ type: 'DELETE', payload: this });
      constructor.clearCache();
    } catch (e) {
      if (!force) {
        alert('Une erreur est survenue, impossible de supprimer cet élément');
      }
      console.error(e);
    }
  }

  static connect(name: string, params: any = {}) {
    const model = this;

    type ConnectedComponentProps = {};

    type ConnectedComponentState = {
      query: any;
      loading: boolean;
      ids: string[];
      pageSize: number;
      page: number;
      count?: number;
      sort?: any;
    };

    return (Component: React.ComponentClass) => {
      return class ConnectedComponent extends React.Component<ConnectedComponentProps, ConnectedComponentState> {
        unsubscribe: any;

        data: any;

        prevLength: number;

        constructor(props: ConnectedComponentProps) {
          super(props);

          const defaultState: ConnectedComponentState = {
            query: {},
            loading: false,
            ids: [],
            count: undefined,
            pageSize: 10,
            page: 1,
            sort: undefined
          }

          if (params.init && typeof params.init === "object") {
            this.state = Object.assign({}, defaultState, params.init);
          } else {
            this.state = defaultState;
          }

          this.prevLength = model.list.length;
        }

        componentDidMount() {
          this.unsubscribe = model.store.subscribe(async () => {
            const newList = model.store.getState().list;
            if (this.prevLength !== newList.length) {
              this.fetchData();
              this.prevLength = newList.length;
            }
            this.forceUpdate();
          });

          if (params.init) {
            this.fetchData();
          }
        }

        componentDidUpdate(prevProps: ConnectedComponentProps, prevState: ConnectedComponentState) {
          if (
            !isEqual(this.state.query, prevState.query) ||
            !isEqual(this.state.page, prevState.page) ||
            !isEqual(this.state.pageSize, prevState.pageSize) ||
            !isEqual(this.state.sort, prevState.sort)
            ) {
            if (this.state.query) {
              return this.fetchData();
            }
            // if (this.state.ids.length >= 1) {
            //   this.setState({ ids: [] });
            // }
          }
          return true;
        }

        componentWillUnmount() {
          this.unsubscribe && this.unsubscribe();
        }

        getList() {
          const modelList = model.list;
          return this.state.ids.map((id) => modelList.find((item: PawModel) => item.id === id)).filter(Boolean);
        }

        configure = async ({ query, page, pageSize, sort }: any, forceReload = false) => {
          const newState: any = {};

          if (query !== undefined && (forceReload || !isEqual(query, this.state.query))) {
            newState.query = query;
          }

          if (page !== undefined && (forceReload || !isEqual(page, this.state.page))) {
            newState.page = page;
          }

          if (pageSize !== undefined && (forceReload || !isEqual(pageSize, this.state.pageSize))) {
            newState.pageSize = pageSize;
          }

          if (sort !== undefined && (forceReload || !isEqual(sort, this.state.sort))) {
            newState.sort = sort;
          }


          Object.keys(newState).forEach((key: string) => {
            if (newState[key] === null) {
              newState[key] = undefined;
            }
          })

          this.setState(newState);
        };

        fetchData = (_query = this.state.query, refresh = false) => {
          this.setState({ loading: true });
          return new Promise(async (resolve) => {
            let ids = [];
            let count;
            if (_query && typeof _query === 'object') {
              const { page, pageSize: per_page, sort } = this.state;
              const query = Object.assign({}, _query, { page, per_page, sort })
              const res = await model.fetch(query, true, refresh);
              count = res.count;
              ids = res.data?.map((item: any) => item.id) || [];
            } else if (typeof _query === 'string') {
              const data = await model.get(_query, true);
              ids = data ? [data.id] : [];
            }
            this.setState({ ids, count, loading: false }, resolve);
          });
        };

        handleNextPage = () => {
          this.configure({ page: this.state.page + 1 });
        }

        handlePrevPage = () => {
          if (this.state.page <= 1) {
            return;
          }

          this.configure({ page: this.state.page - 1 });
        }

        handleRefresh = () => {
          this.fetchData(this.state.query, true);
        }

        render() {
          const props: object = {
            ...this.props,
            [name]: {
              __connected: this,
              list: this.getList(),
              count: this.state.count,
              current: {
                page: this.state.page,
                pageSize: this.state.pageSize
              },
              refresh: this.handleRefresh,
              query: this.state.query,
              loading: this.state.loading,
              configure: this.configure,
              nextPage: this.handleNextPage,
              prevPage: this.handlePrevPage
            },
          };

          return React.createElement(Component, props);
        }
      };
    };
  }
}

export default PawModel;
